import * as React from 'react';

import { emptyObject } from 'bones/constants/empty';
import { MimeType } from 'bones/constants/mimeTypes';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { Attribute, VoiceboxPanelData } from 'bones/constants/voicebox/panel';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { useWithLoading } from 'bones/utils/hooks/useWithLoading';
import { asyncNoop } from 'bones/utils/noop';
import { makeStardogQueryName } from 'bones/utils/voicebox/stardogQuery';
import { usePortalVoiceboxAttributeItemsFetcher } from 'bones/utils/voicebox/usePortalVoiceboxAttributeItemsFetcher';
import {
  QueryArgs,
  ResultBindings,
  usePortalVoiceboxStardogQuery,
} from 'bones/utils/voicebox/usePortalVoiceboxStardogQuery';

const parseBody = async (
  body: ResultBindings<{
    predicate: string;
    count: string;
  }>
) => {
  const bindings = body?.results?.bindings || [];
  return bindings.reduce<Array<{ predicateIri: string; count: number }>>(
    (acc, binding) => {
      if (binding.predicate?.value && binding.count?.value) {
        const count = parseInt(binding.count.value, 10);
        if (count) {
          acc.push({
            count,
            predicateIri: binding.predicate.value,
          });
        }
      }
      return acc;
    },
    []
  );
};

const getAttributesForModelConceptQueryArgs = (
  iri: string
): QueryArgs<
  ResultBindings<{
    predicate: string;
    count: string;
  }>,
  Array<{ predicateIri: string; count: number }>
> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Attributes for Model Concept', iri)}
SELECT
  ?predicate
  (count(DISTINCT ?attribute) as ?count)
WHERE {
  <${iri}> ?predicate ?attribute .
  FILTER(isLiteral(?attribute))
}
GROUP BY ?predicate
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  querySchema: true,
  parseBody,
});

const getAttributesForInstanceQueryArgs = (
  iri: string,
  attributeIris: string[]
): QueryArgs<
  ResultBindings<{
    predicate: string;
    count: string;
  }>,
  Array<{ predicateIri: string; count: number }>
> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Attributes for Instance', iri)}
SELECT
  ?predicate
  (count(DISTINCT ?attribute) as ?count)
WHERE {
  VALUES ?predicate { ${attributeIris
    .map((relIri) => `<${relIri}>`)
    .join(' ')} }
  <${iri}> ?predicate ?attribute .
  FILTER(isLiteral(?attribute))
}
GROUP BY ?predicate
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  parseBody,
});

export const usePortalVoiceboxAttributesFetcher = (
  connectionIdx: number,
  limit: number,
  settingsContext: VoiceboxSettingsContextValue,
  fetchLabels: (iris: string[]) => Promise<void>,
  setAttributesForIri: (
    iri: string,
    action: React.SetStateAction<VoiceboxPanelData['attributes']>
  ) => void
) => {
  const { modelsByDatabaseId, settings } = settingsContext;
  const modelsById = modelsByDatabaseId[settings.databaseId] || emptyObject;
  const { classesByIri, propertiesByIri } = modelsById[
    settings.model || DEFAULT_MODEL_NAME
  ] || {
    classesByIri: emptyObject,
    propertiesByIri: emptyObject,
  };

  const isMounted = useIsMounted();
  const withLoading = useWithLoading('usePortalVoiceboxAttributesFetcher');

  const fetchAttributesForModelConcept = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getAttributesForModelConceptQueryArgs
  );

  const fetchAttributesForInstance = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getAttributesForInstanceQueryArgs
  );

  const setAttributeItems = React.useCallback(
    (
      { iri, predicateIri }: { iri: string; predicateIri: string },
      action: React.SetStateAction<Attribute>
    ) => {
      setAttributesForIri(iri, (prev) => ({
        ...prev,
        attributes: prev.attributes.map((item) => {
          if (item.predicateIri === predicateIri) {
            return typeof action === 'function'
              ? action(item)
              : /* istanbul ignore next */ action;
          }

          // istanbul ignore next
          return item;
        }),
      }));
    },
    [setAttributesForIri]
  );

  const fetchAttributeItems = usePortalVoiceboxAttributeItemsFetcher(
    connectionIdx,
    limit,
    settingsContext,
    setAttributeItems
  );

  return React.useCallback(
    async (iri: string, types: string[]) => {
      const isInstance = Boolean(!classesByIri[iri]);
      const attributeIris = isInstance
        ? Object.values(propertiesByIri)
            .filter(
              ({ domains, isDatatype }) =>
                isDatatype &&
                (!domains.size || types.find((typeIri) => domains.has(typeIri)))
            )
            .map(({ iri }) => iri)
        : [];
      if (isInstance && !attributeIris.length) {
        return null;
      }

      return withLoading(
        (loading) => setAttributesForIri(iri, (prev) => ({ ...prev, loading })),
        async () => {
          const attributeCounts = isInstance
            ? await fetchAttributesForInstance(iri, attributeIris)
            : await fetchAttributesForModelConcept(iri);

          if (!attributeCounts?.length || !isMounted.current) {
            return null;
          }

          const attributes = attributeCounts.map(({ predicateIri, count }) => {
            const hasMore = count > 0;
            return {
              predicateIri,

              // to be filled out by fetchAttributeItems below
              items: [],
              count,
              loading: false,

              hasMore,
              loadingMore: false,
              loadMore: asyncNoop,
            };
          });

          // fetch labels first to avoid displaying local name before label is loaded
          await fetchLabels(
            attributes.map((attribute) => attribute.predicateIri)
          );

          setAttributesForIri(iri, (prev) => ({
            ...prev,
            attributes,
          }));

          await Promise.all(
            // fetch initial sub-items per attribute
            attributes.map(({ hasMore, predicateIri }) => {
              return hasMore
                ? // this calls setAttributeItems which sets a new loadMore
                  fetchAttributeItems({ iri, predicateIri }, 0)
                : // istanbul ignore next -- hasMore is always set since parseBody should only return with count > 0
                  null;
            })
          );

          return attributes.map(({ predicateIri }) => predicateIri);
        }
      );
    },
    [
      classesByIri,
      fetchAttributeItems,
      fetchAttributesForInstance,
      fetchAttributesForModelConcept,
      fetchLabels,
      isMounted,
      propertiesByIri,
      setAttributesForIri,
      withLoading,
    ]
  );
};
